import { Component, Inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { User } from '../../models/user';
import { ErrorService } from '../../services/error.service';
import { FileService } from '../../services/file.service';
import { InvoicesService } from '../../services/invoices.service';
import { SnackbarService } from '../../services/snackbar.service';
import { SpinnerService } from '../../services/spinner.service';
import { SubscriptionService } from '../../services/subscription.service';
import { UserService } from '../../services/user.service';
import { ValidatorService } from '../../services/validator.service';

@Component({
	selector: 'app-email-invoice-modal',
	templateUrl: './email-invoice-modal.html',
	styles: []
})
export class EmailInvoiceModalComponent implements OnInit, OnDestroy {
	public customer: any;
	public customerId: any;
	public form: UntypedFormGroup
	public invoice: any;
	public subscriptions: any[] = [];
	public user: User = new User();
  public subscriptionsUnsubscribe = new Subject<void>();

	constructor(private _userService: UserService,
				private _invoicesService: InvoicesService,
				private _errorService: ErrorService,
				private _vcRef: ViewContainerRef,
				private _fileService: FileService,
				public dialogRef: MatDialogRef<EmailInvoiceModalComponent>,
				@Inject(MAT_DIALOG_DATA) public data: any,
				private _formBuilder: UntypedFormBuilder,
				private _spinnerService: SpinnerService,
				private _snackbarService: SnackbarService,
				private _validatorService: ValidatorService,
				private _subscriptionService: SubscriptionService) {
		this.invoice = this.data.invoice;
		this.customerId = this.data.customer.id;
		this.customer = this.data.customer;
	}

	close(response?) {
		this.dialogRef.close(response);
	}

	getInvoice() {
		this._spinnerService.startSpinner();
		this._invoicesService.getInvoice(this.customer.id, this.invoice).subscribe((response: any) => {
			const file = new Blob([response], {type: 'application/pdf'});
			if ((window.navigator as any).msSaveOrOpenBlob) { // IE and Edge functionality
				(window.navigator as any).msSaveOrOpenBlob(file, 'Invoice-' + this.invoice + '.pdf');
			} else {
				this._fileService.openFileNewWindow(file);
			}
			this._spinnerService.stopSpinner();
		}, (error) => {
			this._spinnerService.stopSpinner();
			if (error.error instanceof Blob) {
				const file = new Blob([error.error], {type: error.error.type});
				const fr = new FileReader();
				fr.onload = (e: any) => {
					if (error.error.type === 'application/json') {
						error = JSON.parse(e.target.result);
					}
					this._errorService.error(error, 'getInvoice failed', 'modal');
				};
				fr.readAsText(file);
			} else {
				this._errorService.error(error, 'getInvoice failed', 'modal');
			}
		});
	}

	ngOnDestroy() {
		this._subscriptionService.unSubscribe(this.subscriptions);
    this.subscriptionsUnsubscribe.next();
	}

	ngOnInit() {
		this.form = this._formBuilder.group({
			email: ['', [Validators.email]],
			emails: [[], [this._validatorService.minArrayLength(1)]],
			subject: [`Invoice # ${this.invoice}`, [Validators.required]],
			message: [`${this.customer.customerName} has sent you Invoice #${this.invoice}. If you have any questions, please contact your carrier.`, [Validators.required]]
		});
    this.getInvoiceEmail();
		this.subscriptions.push(this._userService.user$.subscribe((data) => {
			this.user = data;
		}));
		this.subscriptions.push(this.form.get('emails').valueChanges.subscribe((value) => {
			if (value?.length === 10) {
				this.form.get('email').disable();
			} else if (this.form.get('email').disabled && value?.length < 10) {
				this.form.get('email').enable();
			}
		}));
	}

	submit() {
		if (this.form.valid) {
			this._spinnerService.startSpinner();
			const emailInvoice = {
				emailAddresses: this.form.value.emails,
				invoiceNumber: this.invoice,
				customerId: this.customerId,
				subject: this.form.value.subject,
				body: this.form.value.message
			};
			this._invoicesService.emailInvoice(emailInvoice).subscribe(() => {
				this._spinnerService.stopSpinner();
				this.close('success');
			}, error => {
				this._spinnerService.stopSpinner();
				this._errorService.error(error, 'email invoice failed', 'modal');
			});
		} else {
			if (this.form.get('emails').errors) {
				this.form.get('email').setErrors({minLength: true});
				this.form.get('email').markAsTouched();
			}
			this._snackbarService.openSnackBar('Please fill out all required fields.', 'Close', 3000);
		}
	}

  getInvoiceEmail() {
    this._spinnerService.startSpinner();
    this._invoicesService.getInvoiceEmail(this.customerId, this.invoice)
    .pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((result: any) => {
        this._spinnerService.stopSpinner();
        if (result) {
          this.form.controls.subject.setValue(result?.subject);
          this.form.controls.message.setValue(result?.body);
        }
      }, error => {
        this._spinnerService.stopSpinner();
        this._errorService.error(error, 'get Invoice Email', 'modal');
      });
  }
}
